import { useMemo } from "react";
import { useRole } from "@/data/RoleContext";
import { useAppData } from "@/data/AppDataContext";
import { useCurrentRep } from "@/hooks/useCurrentRep";
import type { Job, Issue } from "@/data/types";
import type { Quote } from "@/data/quoteTypes";

/**
 * Applies role-based data isolation.
 * Sales reps only see their own data.
 * All other roles see everything.
 */
export function useDataIsolation() {
  const { role } = useRole();
  const { jobs, quotes, issues, ...rest } = useAppData();
  const { repName: CURRENT_REP } = useCurrentRep();

  return useMemo(() => {
    if (role !== "sales") {
      return { jobs, quotes, issues, ...rest };
    }

    // Sales rep isolation
    const filteredJobs = jobs.filter(
      (j) => j.salesRep === CURRENT_REP || j.assignedCrew === CURRENT_REP
    );
    const filteredQuotes = quotes.filter((q) => q.createdBy === CURRENT_REP);
    const myJobIds = new Set(filteredJobs.map((j) => j.id));
    const filteredIssues = issues.filter((i) => myJobIds.has(i.jobId));

    return {
      jobs: filteredJobs,
      quotes: filteredQuotes,
      issues: filteredIssues,
      ...rest,
    };
  }, [role, jobs, quotes, issues, rest, CURRENT_REP]);
}

/**
 * Filters a customer list to only show customers related to the sales rep's data.
 */
export function useCustomerIsolation(customerNames: string[]): string[] {
  const { role } = useRole();
  const { jobs, quotes } = useAppData();
  const { repName: CURRENT_REP } = useCurrentRep();

  return useMemo(() => {
    if (role !== "sales") return customerNames;

    const myCustomers = new Set<string>();
    jobs.forEach((j) => {
      if (j.salesRep === CURRENT_REP) myCustomers.add(j.customerName);
    });
    quotes.forEach((q) => {
      if (q.createdBy === CURRENT_REP) myCustomers.add(q.customerName);
    });

    return customerNames.filter((n) => myCustomers.has(n));
  }, [role, customerNames, jobs, quotes, CURRENT_REP]);
}
